/* utility functions shared between activity and notification files */
import React, {Fragment} from 'react';

import get from 'lodash/get';
import mapValues from 'lodash/mapValues';
import moment from 'moment-tz';
import styled from 'styled-components';

import {CommonActionArgs, TargetActionType} from 'app/activity/types/activity';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {getContentType, printContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import type {ActivityType} from 'app/activity/types/activity';
import type {VerbMixinType} from 'app/activity/types/verbs';
import type {NotificationType} from 'app/notifly/types';

export const StyledGeneralDescriptionSpan = styled.span`
  .actors,
  .to_user,
  .target,
  .num_invites,
  .step,
  .assigned,
  .role,
  .audience {
    font-weight: bold;
  }

  .target-description {
    font-style: italic;
    font-weight: normal;
  }
`;

export const VERB_ICONS = {
  actions: 'fa fa-download',
  created: 'fa fa-plus',
  cancel: 'fa fa-plus',
  attempted: 'fa fa-check',
  exported: 'fa fa-download',
  invited: 'far fa-envelope',
  'checked off': 'far fa-check-square',
  'entered data': 'fa fa-pen',
  started: 'fa fa-play',
  revoked: 'fa fa-times',
  approved: 'far fa-check',
  overdue: 'far fa-calendar-times',
  unchecked: 'far fa-square',
  assigned: 'fa fa-user',
  restored: 'fa fa-undo',
  reminded: 'fa fa-bell',
  reviewed: 'fa fa-certificate',
  promoted: 'fa fa-trophy',
  billing_admin: 'fa fa-trophy',
  changed_billing_admin: 'fa fa-trophy',
  edited: 'fa fa-pen',
  broadcast: 'fa fa-bullhorn',
  visited: 'fa fa-eye',
  confirmed: 'far fa-thumbs-up',
  'edited a draft': 'fa fa-pen',
  trashed: 'fa fa-trash',
  'permanently deleted': 'fa fa-trash',
  'requested approval': 'fa fa-pen',
  demoted: 'fa fa-times',
  'closed account': 'fa fa-times',
  'commented on': 'far fa-comments',
  'has completed': 'fa fa-check',
  'employee overdue': 'fa fa-bell',
  received: 'fa fa-folder-open',
  'received one': 'fa fa-folder-open',
  'received failed': 'fa fa-folder-open',
  bulk_invited: 'fa fa-user-plus',
  review_due: 'far fa-calendar-times',
  'future delete': 'fa fa-trash',
  'deleted comment from': 'fa fa-trash',
  imported: 'fa fa-upload',
  mentioned: 'far fa-at',
  knowledge_feedback: 'fa fa-book-open',
  role_promoted: 'fa fa-envelope',
  role_demoted: 'fa fa-envelope',
  'transferred account ownership': 'fa fa-trophy',
  'requested signoff': 'fas fa-signature',
};

function maybeAddLink(
  text,
  url,
  attrs: {
    className?: string;
  } = Object.freeze({})
) {
  if (url != null) {
    return (
      <FullHTMLLink html_url={url} {...attrs}>
        {text}
      </FullHTMLLink>
    );
  }

  return <span {...attrs}>{text}</span>;
}

export function getKeyFormat(
  value: string | number | undefined,
  key: keyof CommonActionArgs,
  activity: ActivityType | NotificationType
) {
  if (value == null) {
    return null;
  }
  const {target} = activity;
  let action_object = activity.action_object || null;

  const targetURL = get(action_object, 'html_url', get(target, 'html_url'));

  switch (key) {
    case 'target': {
      return (
        <Fragment>
          {maybeAddLink(value, targetURL, {
            className: key,
          })}
          {printContentType(target.content_type, 'target-ct')}
        </Fragment>
      );
    }

    case 'quiz': {
      const resultURL = `${targetURL}result/`;
      return (
        <Fragment>
          {maybeAddLink(value, resultURL, {
            className: key,
          })}
          {printContentType(target.content_type, 'target-ct')}
        </Fragment>
      );
    }

    case 'target_role': {
      return (
        <Fragment>
          {maybeAddLink(value, get(target, 'html_url'), {
            className: key,
          })}
          {printContentType(target.content_type, 'target-ct')}
        </Fragment>
      );
    }

    case 'saml_expire_date': {
      return moment(value).format('lll');
    }

    case 'article': {
      return (
        <Fragment>
          {maybeAddLink(value, targetURL, {
            className: key,
          })}

          {action_object != null && action_object.content_type != null
            ? printContentType(action_object.content_type, 'target-ct')
            : null}
        </Fragment>
      );
    }

    case 'data_link': {
      if (typeof value === 'number') {
        return null;
      }
      return <a href={value}>{gt('see attached file')}</a>;
    }

    default:
      return <span className={key}>{value}</span>;
  }
}
export const getActivityOrNotificationDescription = (
  activity: ActivityType | NotificationType,
  forceArgs: TargetActionType | Record<string, never> = {}
) => {
  const {template} = activity.content;
  const args = {...activity.content.args, ...forceArgs};
  const formattedArgs = mapValues(args, (value, key: keyof typeof args) =>
    getKeyFormat(value, key, activity)
  );
  return gt(template, formattedArgs);
};

const listAttrContains = (obj: unknown, list_attr: string, find_value: any) =>
  get(obj, list_attr, []).indexOf(find_value) > -1;

export function getActivityTitleForNotification(notification: VerbMixinType) {
  const args = {
    docType: getContentType(notification.target.content_type).name,
  };

  switch (notification.verb) {
    case 'created':
      return gt('New %(docType)s created', args);

    case 'started':
      return gt('Task started');

    case 'cancel':
      return gt('Task cancelled');

    case 'unchecked':
      return gt('Task step unchecked');

    case 'checked off':
      return gt('Task step checked off');

    case 'employee overdue':
      return 'Employee notified about overdue task';

    case 'entered data':
      return 'Entered data for task';

    case 'edited a draft':
      return gt('New %(docType)s draft created', args);

    case 'overdue':
      return gt('Task fell overdue');

    case 'visited':
      return gt('%(docType)s visited', args);

    case 'commented on':
      return gt('New comment added to %(docType)s', args);

    case 'has completed':
      return gt('Task completed');

    case 'reminded':
      return 'Employee reminded about task start';

    case 'invited':
      return gt('Access to %(docType)s given to you', args);

    case 'bulk_invited':
      return gt('Users invited to account');

    case 'requested approval':
      return gt('%(docType)s draft approval requested', args);

    case 'revoked':
      return gt('Access to %(docType)s revoked', args);

    case 'assigned':
      return gt('A task was assigned to you');

    case 'approved':
      return gt('New version of %(docType)s was approved', args);

    case 'exported':
      return gt('Export of procedures and policies is ready');

    case 'received':
      return gt('Product bundle received');

    case 'received one':
      return gt('Product document received');

    case 'received failed':
      return gt('Product bundle transfer failed');

    case 'promoted':
      return gt('Employee promoted');

    case 'demoted':
      return gt('Employee demoted');

    case 'broadcast':
      return gt('Message from SweetProcess staff received');

    case 'review_due':
      return gt('%(docType)s review is due', args);

    case 'future delete':
      return gt('Account scheduled for deletion');

    case 'imported':
      return gt('Document imported');

    case 'mentioned':
      return gt('You were mentioned');

    case 'edited': {
      const data = notification.additional_data;

      if (listAttrContains(data, 'updated_fields', 'next_review_at')) {
        if (listAttrContains(data, 'set_as_null', 'next_review_at')) {
          return gt('Reviews disabled for %(docType)s', args);
        }

        return gt('Reviews enabled for %(docType)s', args);
      }

      return gt('%(docType)s edited', args);
    }

    case 'knowledge_feedback':
      return gt('New feedback for knowledge base article');

    case 'role_promoted':
      return gt('Assigned to a role');

    case 'role_demoted':
      return gt('Removed from a role');

    default:
      return notification.verb;
  }
}
